<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="step-up-transformer-search-view-edit-header"
    />

    <div v-else class="mb-3">
      <div class="flex align-items-center gap-4">
        <div v-if="isHps" class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            input-id="enable-step-up-filter"
            binary
          />
          <label for="enable-step-up-filter">
            Filter and Sort Transformers
          </label>
        </div>
      </div>
    </div>

    <div
      v-if="noContextForFilter"
      class="no-recommendation-message-container"
      data-cy="step-up-no-context-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No transformer recommendations can be made.
        </p>
        <p class="no-recommendation-body">
          Please select a motor to see step-up transformer recommendations, or
          un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Transformers"
          </span>
          to view all options.
        </p>
      </div>
    </div>

    <div
      v-else-if="noStepUpsWithFilter"
      class="no-recommendation-message-container"
      data-cy="step-up-no-results-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No step-up transformers match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a transformer, please un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Transformers"
          </span>
          to view all available options.
        </p>
      </div>
    </div>

    <EquipmentTable
      v-else
      data-cy="step-up-transformer-search-view-table"
      @select="$emit('select', $event.data as Step_Up_Transformers)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, inject, watch } from "vue";
import { useStepUpTransformers } from "@/composables/equipment/stepUpTransformers/suts";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Step_Up_Transformers } from "gql/api";
import Checkbox from "primevue/checkbox";
import {
  equipmentFormKey,
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Step_Up_Transformers): void;
}>();

const sutsearch = useStepUpTransformers(props.isAdmin);

const scenarioForm = !props.isAdmin ? inject(scenarioFormKey, null) : null;
const scenarioCalculations = !props.isAdmin
  ? inject(scenarioCalculationsKey, null)
  : null;
const equipmentForm = !props.isAdmin ? inject(equipmentFormKey, null) : null;

const isHps = computed(() => {
  if (!scenarioForm) return false;
  return scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const enableFilter = ref<boolean>(isHps.value);

// Ensure HPS scenarios default to having recommendations enabled even if
// scenario data loads asynchronously, without overriding user toggles.
watch(
  isHps,
  value => {
    if (value && !enableFilter.value) {
      enableFilter.value = true;
    }
  },
  { immediate: false }
);

const isMotorSelected = computed(() => {
  if (!equipmentForm) return false;
  const eq = equipmentForm.data.value;
  return !!(eq.motor_id || eq.lower_motor_id);
});

const sortedStepUps = computed(() => {
  const rows = sutsearch.allRows.value;

  // Preserve undefined while loading so the table can show loading state.
  if (!rows) {
    return rows;
  }

  if (props.isAdmin || !isHps.value || !enableFilter.value) {
    return rows;
  }

  if (!isMotorSelected.value) {
    return [];
  }

  if (!scenarioCalculations) {
    return rows;
  }

  const surfaceDriveCalcs = scenarioCalculations.surfaceDriveCalculations;
  const voltageForRange = surfaceDriveCalcs.requiredVoltageV.value;
  const requiredKva =
    surfaceDriveCalcs.requiredKVA60Hz.value ||
    surfaceDriveCalcs.requiredKVA.value;
  // Prefer catalog kVA <= 1.25× required; if discrete sizes skip that band, relax.
  const maxKvaPreferred = requiredKva * 1.25;

  if (!voltageForRange || !requiredKva) {
    return rows;
  }

  const collectPassing = (usePreferredMaxKva: boolean) => {
    const out: Array<{
      row: Step_Up_Transformers;
      kva: number;
      secondaryCenter: number;
      secondarySpan: number;
    }> = [];

    rows.forEach(row => {
      const kva = row.kva;
      const minSec = row.min_secondary_voltage;
      const maxSec = row.max_secondary_voltage;

      if (
        kva === null ||
        kva === undefined ||
        minSec === null ||
        minSec === undefined ||
        maxSec === null ||
        maxSec === undefined
      ) {
        return;
      }

      const withinTapRange = !!(
        voltageForRange >= minSec && voltageForRange <= maxSec
      );

      const kvaOk =
        withinTapRange &&
        kva >= requiredKva &&
        (!usePreferredMaxKva || kva <= maxKvaPreferred);

      if (kvaOk) {
        const secondaryCenter = (minSec + maxSec) / 2;
        const secondarySpan = maxSec - minSec;
        out.push({ row, kva, secondaryCenter, secondarySpan });
      }
    });

    return out;
  };

  let passing = collectPassing(true);
  if (passing.length === 0) {
    passing = collectPassing(false);
  }

  if (passing.length === 0) {
    return [];
  }

  // Rank by voltage fit first, then by lowest passing kVA.
  passing.sort((a, b) => {
    const aDiff = Math.abs(a.secondaryCenter - voltageForRange);
    const bDiff = Math.abs(b.secondaryCenter - voltageForRange);
    if (aDiff !== bDiff) return aDiff - bDiff;
    if (a.kva !== b.kva) return a.kva - b.kva;
    return a.secondarySpan - b.secondarySpan;
  });

  return passing.map(entry => entry.row);
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = sutsearch.filteredRows.value;
  const sorted = sortedStepUps.value;

  if (!baseFiltered || !sorted) {
    return sorted as typeof baseFiltered;
  }

  const filteredIds = new Set(baseFiltered.map(p => p.id));
  return sorted.filter(stepUp => filteredIds.has(stepUp.id));
});

const noContextForFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const hasLoadedRows = Array.isArray(sutsearch.allRows.value);
  return filterOn && hasLoadedRows && !isMotorSelected.value;
});

const noStepUpsWithFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const count = sortedFilteredRows.value?.length ?? 0;
  const hasLoadedRows = Array.isArray(sutsearch.allRows.value);
  return filterOn && hasLoadedRows && count === 0 && isMotorSelected.value;
});

const equipmentWithSortedStepUps = {
  ...sutsearch,
  allRows: sortedStepUps,
  filteredRows: sortedFilteredRows
};

provide("equipment", equipmentWithSortedStepUps);
</script>
